import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { commandCenterApi } from '../services/api'
import styles from './KambiOutcomeSelector.module.css'

function numericValue(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function displayName(selection) {
  const meta = selection?.selection_metadata || {}
  return selection?.name || meta.contract_title || meta.team_abbrev || meta.team_name || meta.individual_name || 'Contract'
}

function eventLabel(selection) {
  return selection?.event_name || selection?.event_metadata?.title || 'Event'
}

function marketLabel(selection) {
  return selection?.market_name || selection?.selection_metadata?.market_title || 'Market'
}

function percentLabel(selection) {
  const meta = selection?.selection_metadata || {}
  const chance = numericValue(meta.chance)
  if (chance !== null) return `${Math.round(chance)}%`

  const yesPrice = numericValue(meta.yes_price)
  if (yesPrice !== null) return `${Math.round(yesPrice * 100)}%`

  const noChance = numericValue(meta.no_chance)
  if (noChance !== null) return `No ${Math.round(noChance)}%`

  const noPrice = numericValue(meta.no_price)
  if (noPrice !== null) return `No ${Math.round(noPrice * 100)}%`

  return '-'
}

function contractLabel(selection) {
  return `${eventLabel(selection)} - Trade ${displayName(selection)} - ${percentLabel(selection)}`
}

function symbolLabel(selection) {
  return selection?.selection_metadata?.symbol || selection?.external_id || ''
}

function searchResultTitle(result) {
  if (result?.source_type === 'contract' && result.event_title) {
    return `${result.event_title} - ${result.title}`
  }
  return result?.title || 'OG result'
}

function searchResultTypeLabel(result) {
  const type = String(result?.source_type || '').toLowerCase()
  if (type === 'event') return 'Event'
  if (type === 'contract') return 'Contract'
  if (!type) return 'Result'
  return type.charAt(0).toUpperCase() + type.slice(1)
}

function searchResultActionLabel(result) {
  return String(result?.source_type || '').toLowerCase() === 'contract' ? 'Select OG contract' : 'Open OG event'
}

function buildContractCache(groups = []) {
  const entries = []
  for (const group of groups) {
    for (const contract of group.contracts || []) {
      if (contract.external_id) entries.push([contract.external_id, contract])
    }
  }
  return Object.fromEntries(entries)
}

export default function OGContractSelector({
  orgSlug,
  providerSelectionIds,
  onProviderSelectionIdsChange,
  onSelectedContractsChange,
}) {
  const [searchInput, setSearchInput] = useState('')
  const [searchResponse, setSearchResponse] = useState(null)
  const [response, setResponse] = useState(null)
  const [contractCache, setContractCache] = useState({})
  const trimmedSearchInput = searchInput.trim()

  const notifySelection = (nextIds, nextCache = contractCache) => {
    onProviderSelectionIdsChange(nextIds)
    const nextContracts = nextIds.map((id) => nextCache[id]).filter(Boolean)
    onSelectedContractsChange?.(nextContracts)
  }

  const loadContracts = useMutation({
    mutationFn: (lookup) => commandCenterApi.getOGEventContracts(
      orgSlug,
      typeof lookup === 'string' ? lookup : lookup?.event_id,
    ),
    onSuccess: (data, lookup) => {
      const loadedCache = buildContractCache(data?.groups || [])
      const nextCache = {
        ...contractCache,
        ...loadedCache,
      }
      setResponse(data)
      setContractCache(nextCache)

      const contractID = typeof lookup === 'object' ? lookup?.contract_id : ''
      const selectedContract = contractID ? loadedCache[contractID] : null
      notifySelection(selectedContract?.selectable ? [contractID] : [], nextCache)
    },
  })

  const searchPredictions = useMutation({
    mutationFn: () => commandCenterApi.searchOGPredictions(orgSlug, trimmedSearchInput, 20),
    onSuccess: (data) => setSearchResponse(data),
  })

  const selectedContracts = useMemo(
    () => providerSelectionIds.map((id) => contractCache[id]).filter(Boolean),
    [providerSelectionIds, contractCache],
  )

  const selectedByExternalID = new Set(providerSelectionIds)

  const selectContract = (contract) => {
    if (!contract?.external_id || !contract.selectable) return
    const nextCache = { ...contractCache, [contract.external_id]: contract }
    setContractCache(nextCache)
    const nextIds = selectedByExternalID.has(contract.external_id) ? [] : [contract.external_id]
    notifySelection(nextIds, nextCache)
  }

  const removeContract = (externalID) => {
    notifySelection(providerSelectionIds.filter((id) => id !== externalID))
  }

  const loadSearchResult = (result) => {
    if (!result?.event_id) return
    setSearchResponse(null)
    loadContracts.mutate(result)
  }

  const event = response?.event
  const groups = response?.groups || []
  const visibleGroups = groups.filter((group) => (group.contracts || []).some((contract) => contract.selectable))
  const searchResults = searchResponse?.results || []

  return (
    <div className={styles.container}>
      <div className={`${styles.section} ${styles.ogSearchSection}`}>
        <label className={styles.sectionLabel}>Search OG</label>
        <div className={styles.inputRow}>
          <input
            className={styles.outcomeInput}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search teams, players, events, or markets"
          />
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => searchPredictions.mutate()}
            disabled={!orgSlug || !trimmedSearchInput || searchPredictions.isPending}
          >
            {searchPredictions.isPending ? 'Searching...' : 'Search'}
          </button>
        </div>

        {searchPredictions.error ? (
          <div className={styles.errorMsg}>{searchPredictions.error.response?.data?.error || 'Failed to search OG'}</div>
        ) : null}

        {searchResults.length > 0 ? (
          <div className={styles.ogSearchResults}>
            {searchResults.map((result) => {
              const title = searchResultTitle(result)
              const metaParts = [
                result.event_kind_group_display_name,
                result.live_probability,
                searchResultTypeLabel(result),
              ].filter(Boolean)
              return (
                <button
                  key={`${result.source_type}-${result.id}-${result.event_id}`}
                  type="button"
                  className={styles.ogSearchResult}
                  onClick={() => loadSearchResult(result)}
                  aria-label={`${searchResultActionLabel(result)} ${title}`}
                  disabled={loadContracts.isPending}
                >
                  <span className={styles.ogSearchTitle}>{title}</span>
                  {metaParts.length ? <span className={styles.ogSearchMeta}>{metaParts.join(' - ')}</span> : null}
                </button>
              )
            })}
          </div>
        ) : searchResponse ? (
          <div className={styles.validatingMsg}>No OG results found.</div>
        ) : null}
      </div>

      {providerSelectionIds.length > 0 ? (
        <div className={styles.chipList}>
          {providerSelectionIds.map((id) => {
            const selection = selectedContracts.find((item) => item.external_id === id)
            return (
              <span key={id} className={`${styles.chip} ${styles.cryptoChip}`}>
                {selection ? contractLabel(selection) : id}
                <button type="button" className={styles.chipRemove} onClick={() => removeContract(id)}>
                  &times;
                </button>
              </span>
            )
          })}
        </div>
      ) : null}

      {loadContracts.error ? (
        <div className={styles.errorMsg}>{loadContracts.error.response?.data?.error || 'Failed to load OG contracts'}</div>
      ) : null}

      {event ? (
        <div className={styles.ogEventSummary}>
          <div className={styles.ogEventTitle}>{event.name}</div>
          <div className={styles.ogEventMeta}>
            <span>{event.metadata?.event_kind_display_name || event.metadata?.event_kind || 'OG'}</span>
            <span>{event.status}</span>
            {event.metadata?.close_date ? <span>Closes {new Date(event.metadata.close_date).toLocaleString()}</span> : null}
          </div>
        </div>
      ) : null}

      {visibleGroups.length > 0 ? (
        <div className={`${styles.validationPreview} ${styles.cryptoContractResults}`}>
          {visibleGroups.map((group, index) => {
            const groupHasSelected = (group.contracts || []).some((contract) => selectedByExternalID.has(contract.external_id))
            return (
              <details
                key={group.key}
                className={`${styles.eventCard} ${styles.cryptoContractCard} ${styles.ogMarketGroup}`}
                open={index === 0 || groupHasSelected}
              >
                <summary className={styles.ogGroupHeader}>
                  <span>{group.title}</span>
                  <span>{group.selectable_count}/{group.total_count}</span>
                </summary>
                <div className={styles.ogMarketContracts}>
                  {(group.contracts || []).map((contract) => {
                    const selected = selectedByExternalID.has(contract.external_id)
                    const symbol = symbolLabel(contract)
                    const disabled = !contract.selectable
                    return (
                      <button
                        key={contract.id}
                        type="button"
                        className={`${styles.outcomeRow} ${styles.cryptoContractRow} ${disabled ? styles.ogDisabledContract : ''}`}
                        onClick={() => selectContract(contract)}
                        disabled={disabled}
                        aria-pressed={selected}
                        aria-label={`${eventLabel(contract)} Trade ${displayName(contract)} ${marketLabel(contract)} ${percentLabel(contract)}`}
                      >
                        <div className={styles.cryptoContractSummary}>
                          <div className={styles.cryptoContractInfo}>
                            <div className={styles.cryptoEventName}>{eventLabel(contract)}</div>
                            <div className={styles.cryptoBetLine}>
                              <span className={styles.cryptoDirection}>Trade {displayName(contract)}</span>
                              <span className={styles.cryptoMarket}>{marketLabel(contract)}</span>
                            </div>
                          </div>
                          <div className={styles.cryptoPercent}>{percentLabel(contract)}</div>
                        </div>
                        <div className={styles.cryptoContractMeta}>
                          {symbol ? <span title={symbol}>{symbol}</span> : null}
                          {selected ? <span className={styles.betOfferType}>Selected</span> : null}
                          {disabled ? <span className={styles.betOfferType}>{contract.status || 'Unavailable'}</span> : null}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </details>
            )
          })}
        </div>
      ) : groups.length > 0 ? (
        <div className={styles.validatingMsg}>No active OG contracts found for this event.</div>
      ) : response ? (
        <div className={styles.validatingMsg}>No contracts found for this event.</div>
      ) : null}
    </div>
  )
}
